import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2, Save } from 'lucide-react';
import {
  endgoodProcessWeightApiService,
  EndgoodProcessWeightDto,
} from '../services/endgoodProcessWeightApi';
import type { ProcessStep } from '../services/processFlowApi';
import { useAuth } from '../contexts/AuthContext';
import { endgoodApiService } from '../services/endgoodApi';

export interface EndgoodProcessWeightDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  endgoodId: string;
  processFlowId?: string;
  endgoodName: string;
  processSteps: ProcessStep[];
  readOnly?: boolean;
  onSaved?: () => void;
}

export function EndgoodProcessWeightDialog({
  open,
  onOpenChange,
  endgoodId,
  processFlowId,
  endgoodName,
  processSteps,
  readOnly = false,
  onSaved,
}: EndgoodProcessWeightDialogProps) {
  const { user } = useAuth();
  const userId = user?.id;
  const [existingWeights, setExistingWeights] = useState<EndgoodProcessWeightDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalWeight, setFinalWeight] = useState<number | null>(null);
  // processStepId -> weight input value (string for controlled input)
  const [weights, setWeights] = useState<Record<number, string>>({});

  const loadWeights = async () => {
    if (!endgoodId || !processFlowId) return;
    setLoading(true);
    try {
      const [data, endgood] = await Promise.all([
        endgoodProcessWeightApiService.getByEndgoodId(endgoodId, processFlowId),
        endgoodApiService.getEndgoodById(endgoodId),
      ]);
      setExistingWeights(data);
      setFinalWeight(endgood?.productionWeight != null ? endgood.productionWeight : null);
    } catch {
      setExistingWeights([]);
      setFinalWeight(null);
    } finally {
      setLoading(false);
    }
  };

  const sortedProcessSteps = useMemo(
    () => [...(processSteps || [])].sort((a, b) => a.sequenceOrder - b.sequenceOrder),
    [processSteps]
  );

  useEffect(() => {
    if (open && endgoodId) loadWeights();
    if (!open) {
      setWeights({});
      setFinalWeight(null);
    }
  }, [open, endgoodId, processFlowId]);

  // When existing weights or process steps are set, populate weight inputs (existing value or empty)
  useEffect(() => {
    if (sortedProcessSteps.length === 0) return;
    const next: Record<number, string> = {};
    sortedProcessSteps.forEach((ps) => {
      const row = existingWeights.find((w) => w.processStepId === ps.id);
      next[ps.id] = row != null ? String(row.outputWeight) : '';
    });
    setWeights(next);
  }, [existingWeights, sortedProcessSteps]);

  const setWeight = (processStepId: number, value: string) => {
    setWeights((prev) => ({ ...prev, [processStepId]: value }));
  };

  const handleSaveAll = async () => {
    if (!endgoodId) return;
    setSaving(true);
    try {
      const endgoodIdNum = Number(endgoodId);
      for (const ps of sortedProcessSteps) {
        const value = (weights[ps.id] ?? '').trim();
        const num = value === '' ? NaN : parseFloat(value);
        const existing = existingWeights.find((w) => w.processStepId === ps.id);

        if (!Number.isNaN(num) && num >= 0) {
          if (existing) {
            await endgoodProcessWeightApiService.update(String(existing.id), {
              id: existing.id,
              endgoodId: existing.endgoodId,
              processStepId: ps.id,
              outputWeight: num,
              createdBy: userId,
              updatedBy: userId,
            });
          } else {
            await endgoodProcessWeightApiService.create({
              endgoodId: endgoodIdNum,
              processStepId: ps.id,
              outputWeight: num,
              createdBy: userId,
              updatedBy: userId,
            });
          }
        } else if (existing && value === '') {
          await endgoodProcessWeightApiService.delete(String(existing.id));
        }
      }
      await loadWeights();
      onSaved?.();
      onOpenChange(false);
    } catch {
      // toasts in service
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Process output weights</DialogTitle>
          <DialogDescription>
            Enter output weight (in gms) for each process for {endgoodName || 'this endgood'}. Leave empty if not applicable.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : !processFlowId ? (
            <p className="text-center text-muted-foreground py-6">No routing selected. Select a routing first.</p>
          ) : sortedProcessSteps.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">No process steps in this routing. Select a routing with steps first.</p>
          ) : (
            <>
              <div className="rounded-md border border-primary-200 bg-primary-50/50 px-4 py-3">
                <span className="font-medium text-primary-800">Final weight of endgood: </span>
                <span className="text-primary-700">{finalWeight != null ? `${finalWeight} gms` : ''}</span>
              </div>
              <div className="border border-primary-200 rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary-50">
                      <TableHead className="font-semibold text-primary-800">Process</TableHead>
                      <TableHead className="font-semibold text-primary-800 w-40">Weight (gms)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedProcessSteps.map((ps) => (
                      <TableRow key={ps.id}>
                        <TableCell className="font-medium">
                          {ps.process?.processName ?? `Step ${ps.sequenceOrder}`}
                        </TableCell>
                        <TableCell>
                          {readOnly ? (
                            <span className="text-primary-700">{weights[ps.id] ? `${weights[ps.id]} gms` : '—'}</span>
                          ) : (
                            <Input
                              type="text"
                              inputMode="decimal"
                              placeholder=""
                              value={weights[ps.id] ?? ''}
                              onChange={(e) => setWeight(ps.id, e.target.value)}
                              className="border-primary-200 h-9"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {!readOnly && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={handleSaveAll}
                    disabled={saving}
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
